// UI-2: Field and the inputs. design-brief.md "Inputs"; addendum 2.6: 16px on a phone
// (tokens.css sets --text-input to 16px up to 767px), so iOS never zooms the page on focus.
// Select is the native one, styled: it is for long lists; short ones are a ChipGroup.
import React, { useId } from 'react';
import './v2.css';

const cx = (...parts) => parts.filter(Boolean).join(' ');

// Label above, hint or error below, wired to the control by id and aria-describedby.
// The child is one control; Field gives it its id, aria-invalid and aria-describedby.
export function Field({ label, hint, error, required = false, children, className }) {
  const id = useId();
  const controlId = children?.props?.id || `${id}-control`;
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [errorId, hintId, children?.props?.['aria-describedby']].filter(Boolean).join(' ') || undefined;
  return (
    <div className={cx('ui-field', className)}>
      <label className="ui-field__label" htmlFor={controlId}>
        {label}{required && <span aria-hidden="true"> *</span>}
      </label>
      {React.cloneElement(children, {
        id: controlId,
        'aria-describedby': describedBy,
        'aria-invalid': error ? true : children.props['aria-invalid'],
        required: required || children.props.required,
      })}
      {error && <span className="ui-field__error" id={errorId}>{error}</span>}
      {hint && !error && <span className="ui-field__hint" id={hintId}>{hint}</span>}
    </div>
  );
}

export const Input = React.forwardRef(function Input({ className, type = 'text', ...rest }, ref) {
  return <input ref={ref} type={type} className={cx('ui-input', className)} {...rest} />;
});

export const Textarea = React.forwardRef(function Textarea({ className, rows = 4, ...rest }, ref) {
  return <textarea ref={ref} rows={rows} className={cx('ui-input', className)} {...rest} />;
});

// options: [{ value, label }] or children <option>s. placeholder adds an empty first option.
export const Select = React.forwardRef(function Select({ className, options, placeholder, children, ...rest }, ref) {
  return (
    <select ref={ref} className={cx('ui-input', 'ui-select', className)} {...rest}>
      {placeholder !== undefined && <option value="">{placeholder}</option>}
      {options ? options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>) : children}
    </select>
  );
});

// The large underlined title ("Add task", a proposal title). Labelled by aria-label when
// it has no Field around it.
export const TitleInput = React.forwardRef(function TitleInput({ className, ...rest }, ref) {
  return <input ref={ref} type="text" className={cx('ui-title-input', className)} {...rest} />;
});

// Full width on a phone (addendum 4). type="search" gives the clear button and the
// keyboard's search key; aria-label names it when there is no visible label.
export const SearchInput = React.forwardRef(function SearchInput({ className, label = 'Search', ...rest }, ref) {
  return (
    <span className="ui-input-affix ui-input-affix--lead ui-input--search-wrap">
      <span className="ui-input-affix__lead" aria-hidden="true">⌕</span>
      <input ref={ref} type="search" aria-label={rest.id ? undefined : label} className={cx('ui-input', 'ui-input--search', className)} {...rest} />
    </span>
  );
});

export const DateInput = React.forwardRef(function DateInput(props, ref) {
  return <Input ref={ref} type="date" {...props} />;
});

export const TimeInput = React.forwardRef(function TimeInput(props, ref) {
  return <Input ref={ref} type="time" step={300} {...props} />;
});

// Money as text with the decimal keypad (inputMode), not type="number": a number input
// drops "12." while typing, scrolls the value on a wheel and shows spinners.
export const MoneyInput = React.forwardRef(function MoneyInput({ currency = '£', className, ...rest }, ref) {
  return (
    <span className="ui-input-affix ui-input-affix--lead">
      <span className="ui-input-affix__lead" aria-hidden="true">{currency}</span>
      <input ref={ref} type="text" inputMode="decimal" autoComplete="off" className={cx('ui-input', 'ui-input--money', className)} {...rest} />
    </span>
  );
});
